<!--
  DeductionTagDailyView.vue — 预扣款统计页面
  按 统计日期 + 签约渠道 + 人工标签 维度展示每日扣款成功统计与下一日预扣款。
  规则：若上一日同 (channel + tag) 有数据，则上一日的「下一日预扣款」与当日的「实际扣款」并列展示。
  接口：GET /v1/analytics/order/stats/daily/deduction-tag-stat
-->
<template>
  <div>
    <!-- 搜索栏 -->
    <a-card
      :bordered="false"
      style="margin-bottom: 16px"
    >
      <a-form
        layout="inline"
        :model="searchForm"
      >
        <a-form-item label="日期范围">
          <a-range-picker
            v-model:value="dateRange"
            value-format="YYYY-MM-DD"
            :allow-clear="true"
          />
        </a-form-item>
        <a-form-item label="签约渠道">
          <a-select
            v-model:value="searchForm.agreementChannel"
            style="width: 140px"
            placeholder="全部渠道"
            allow-clear
            :options="channelOptions"
          />
        </a-form-item>
        <a-form-item label="人工标签">
          <a-input
            v-model:value="searchForm.tag"
            style="width: 160px"
            placeholder="精确匹配, 如: 默认"
            allow-clear
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button
              type="primary"
              :loading="loading"
              @click="handleSearch"
            >查询</a-button>
            <a-button @click="handleReset">重置</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 数据表格 -->
    <a-card
      :bordered="false"
      title="预扣款统计"
    >
      <template #extra>
        <span style="color: #999; font-size: 12px">
          预扣款值取自前一日同渠道+标签数据的 nextDeduct*，与当日实际扣款并列展示
        </span>
      </template>
      <a-table
        :columns="columns"
        :data-source="tableData"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        :scroll="{ x: 'max-content' }"
        size="small"
        bordered
        @change="handleTableChange"
      />
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { message } from "ant-design-vue";
import { analyticsUrl } from "@/utils/api";
import { get } from "@/utils/request";

const loading = ref(false);
const dateRange = ref([]);
const tableData = ref([]);

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total) => `共 ${total} 条`,
});

const searchForm = reactive({
  startDate: "",
  endDate: "",
  agreementChannel: undefined,
  tag: "",
});

const channelOptions = [
  { label: "合利宝", value: "hlb" },
  { label: "快钱", value: "kq" },
];

const nowrap = { style: { whiteSpace: "nowrap" } };

// ========================
// 工具函数
// ========================

const fmt = (v) =>
  v === null || v === undefined ? "-" : Number(v).toLocaleString();

// 金额: 分 -> 元，保留 2 位小数
const fmtAmount = (v) => {
  if (v === null || v === undefined) return "-";
  return (Number(v) / 100).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// 同时展示「实际 / 预测」
const fmtPair = (actual, predicted) => {
  const a = actual === null || actual === undefined ? "-" : fmt(actual);
  const p =
    predicted === null || predicted === undefined ? "-" : fmt(predicted);
  return `${a} / ${p}`;
};

// 日期 D-1 字符串
const prevDateStr = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// ========================
// 表格列
// ========================

const renderPair = (field) => ({ record }) =>
  fmtPair(record[field], record[`predicted_${field}`]);

const columns = [
  {
    title: "统计日期",
    dataIndex: "statDate",
    key: "statDate",
    width: 110,
    fixed: "left",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "签约渠道",
    dataIndex: "agreementChannelDisplay",
    key: "agreementChannelDisplay",
    width: 90,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "人工标签",
    dataIndex: "tag",
    key: "tag",
    width: 100,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "扣款订单数 (实际/预测)",
    key: "totalOrderPair",
    width: 200,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: renderPair("paidTotalOrderCount"),
  },
  {
    title: "扣款金额(元)",
    dataIndex: "paidTotalAmount",
    key: "paidTotalAmount",
    width: 130,
    align: "right",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => fmtAmount(text),
  },
  {
    title: "M0 订单 (实际/预测)",
    key: "m0Pair",
    width: 180,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: renderPair("paidM0OrderCount"),
  },
  {
    title: "M0 金额(元)",
    dataIndex: "paidM0Amount",
    key: "paidM0Amount",
    width: 130,
    align: "right",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => fmtAmount(text),
  },
  {
    title: "M1 订单 (实际/预测)",
    key: "m1Pair",
    width: 180,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: renderPair("paidM1OrderCount"),
  },
  {
    title: "M1 金额(元)",
    dataIndex: "paidM1Amount",
    key: "paidM1Amount",
    width: 130,
    align: "right",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => fmtAmount(text),
  },
  {
    title: "M2+ 订单 (实际/预测)",
    key: "m2Pair",
    width: 180,
    align: "center",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: renderPair("paidM2OrderCount"),
  },
  {
    title: "M2+ 金额(元)",
    dataIndex: "paidM2Amount",
    key: "paidM2Amount",
    width: 130,
    align: "right",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => fmtAmount(text),
  },
  {
    title: "更新时间",
    dataIndex: "updatedAt",
    key: "updatedAt",
    width: 165,
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
];

// ========================
// 数据合并：将上一日 nextDeduct* 注入到当日的 predicted_* 字段
// ========================

const mergePredicted = (rows) => {
  // 按 (channel + tag + date) 建索引
  const indexMap = new Map();
  for (const r of rows) {
    indexMap.set(`${r.agreementChannel}|${r.tag}|${r.statDate}`, r);
  }
  return rows.map((r) => {
    const prevKey = `${r.agreementChannel}|${r.tag}|${prevDateStr(r.statDate)}`;
    const prev = indexMap.get(prevKey);
    return {
      ...r,
      predicted_paidTotalOrderCount: prev
        ? prev.nextDeductTotalOrderCount
        : null,
      predicted_paidM0OrderCount: prev ? prev.nextDeductM0OrderCount : null,
      predicted_paidM1OrderCount: prev ? prev.nextDeductM1OrderCount : null,
      predicted_paidM2OrderCount: prev ? prev.nextDeductM2OrderCount : null,
    };
  });
};

// ========================
// 数据请求
// ========================

const fetchData = async (pageIdx = 1, pageSize = 20) => {
  loading.value = true;
  try {
    const params = {
      pageIdx,
      pageSize,
      startDate: searchForm.startDate || "",
      endDate: searchForm.endDate || "",
      agreementChannel: searchForm.agreementChannel || "",
      tag: searchForm.tag || "",
    };
    const json = await get(
      analyticsUrl("/order/stats/daily/deduction-tag-stat"),
      params
    );
    if (json.code === 200 && json.data) {
      const d = json.data;
      tableData.value = mergePredicted(d.data || []);
      pagination.total = d.dataSum;
      pagination.current = d.pageIdx;
      pagination.pageSize = d.pageSize;
    } else {
      message.warning("查询失败：" + (json.content || "未知错误"));
    }
  } catch (e) {
    message.error("接口请求失败");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  if (dateRange.value && dateRange.value.length === 2) {
    searchForm.startDate = dateRange.value[0];
    searchForm.endDate = dateRange.value[1];
  } else {
    searchForm.startDate = "";
    searchForm.endDate = "";
  }
  fetchData(1, pagination.pageSize);
};

const handleReset = () => {
  dateRange.value = [];
  searchForm.startDate = "";
  searchForm.endDate = "";
  searchForm.agreementChannel = undefined;
  searchForm.tag = "";
  fetchData(1, pagination.pageSize);
};

const handleTableChange = (pag) => {
  fetchData(pag.current, pag.pageSize);
};

onMounted(() => {
  fetchData();
});
</script>
